import { CircularLoading } from '@components/UI-Components/CircularLoading'
import { ConfirmationDialog } from '@components/UI-Components/ConfirmationDialog'
import { MessageDialog } from '@components/UI-Components/MessageDialog'
import { StandardTableView } from '@components/UI-Components/StandardTableView/container/StandardTableView'
import { config } from '@config'
import Backdrop from '@material-ui/core/Backdrop'
import Portal from '@material-ui/core/Portal'
import { useEnumListByName } from '@modules/wholesale/buyingGroupSummary/graphql/hooks'
import { isWholesaleCMOrAdmin } from '@services/commonServices'
import { orderManagementService } from '@services/wholesale/OrderManagementService'
import { orderManagementTableDefinitionService } from '@services/wholesale/OrderManagementTableDefinitionService'
import { stores } from '@stores'
import { useObserver } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import * as css from './OrderManagementWrapper.css'
import { OrderManagementSection } from '@modules/common/models/enums/OrderManagement'
import { useTranslation } from 'react-i18next'

interface IOrderManagementWrapper {}

const HEADERHEIGHT = 32
const ROWHEIGHT = 31

export const OrderManagementWrapper: React.FunctionComponent<
  IOrderManagementWrapper
> = () => {
  return useObserver(function useHook() {
    const {
      search: { searchKey, clearSearchKey },
      nav: {
        queryParams: { section, view, tab: selectedTab }
      },
      orderManagementStore: {
        deletedOrdersTabView,
        openConfirmationDialog,
        handleConfirmationDialogClose,
        handleOrderDelete,
        isOrderDeleteInProgress,
        orderStatusValidationError,
        orderStatusValidationErrorMessage,
        handleValidationDialogClose,
        showWarningDialog,
        showRestoreConfirmation,
        statusChangeWarningMessage,
        emptyOrderValidationErrorMessage,
        onValidationWarningConfirm,
        handleStatusChangeWarningDialogClose,
        isStatusChangeInProgress,
        isEmptyOrderDeletePopupVisible,
        hideDeleteConfirmationPopup,
        isOrderValidationIsInProgress,
        setSelectedData,
        canExportOrders,
        openMessageDialog,
        statusChangeConfirmationMessage,
        handleMessageDialogClose,
        showBulkStatusUpdateWarningDialog,
        bulkStatusChangeWarningMessage,
        showBulkSendToSAPWarningDialog,
        sendToSAPWarningMessage,
        handleBulkStatusUpdateConfirmationDialogClose,
        handleBulkStatusChange,
        handleSendOrderToSAP,
        setOrderPresent,
        selectedRows,
        getOrders,
        orders
      },
      showroomCollection: { isBackdropOnLoading }
    } = stores

    useEffect(() => {
      getOrders()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section, view, selectedTab])

    // Clear search on unmount
    useEffect(() => {
      return () => {
        clearSearchKey()
        canExportOrders(false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    const { t } = useTranslation()
    const isGMOrAdmin = !!isWholesaleCMOrAdmin()
    const isRTLOrderView = section === OrderManagementSection.retailOrder
    const columnDef =
      orderManagementTableDefinitionService.buildTableViewDefinitionForOrderManagement(
        isGMOrAdmin,
        isRTLOrderView
      )

    const [{ data: enumData }] = [
      useEnumListByName(config?.appConfig?.enumerations?.orderStatus?.NAME ?? null)
    ]

    setOrderPresent(orders?.clientOrders?.length > 0)

    const processedData = orderManagementService.processAndSearchOrderData(
      {
        orders: orders?.clientOrders ?? [],
        statusEnumValues: enumData?.findEnumList?.Items?.[0]?.Values ?? [],
        buyingSessionGroup: orders?.buyingSessionGroup ?? null
      } as any,
      searchKey,
      columnDef,
      t,
      isRTLOrderView
    )

    if (deletedOrdersTabView) {
      canExportOrders(false)
    } else if (orders?.clientOrders) {
      canExportOrders(Boolean(orders.clientOrders.length))
    }

    return (
      <div className={css.orderManagementContainer}>
        <React.Fragment>
          <StandardTableView
            hideLetterNavigation={true}
            colDef={columnDef}
            overScanCount={15}
            data={processedData}
            showBodyRowBorder={true}
            headerHeight={HEADERHEIGHT}
            rowHeight={ROWHEIGHT}
            allowMultiSelect={!deletedOrdersTabView}
            maxSelectionLimit={processedData.length}
            setSelectedData={setSelectedData}
            canSelectAll={true}
            selectedRows={selectedRows}
          />
          {openConfirmationDialog ||
          showWarningDialog ||
          showBulkStatusUpdateWarningDialog ||
          showBulkSendToSAPWarningDialog ? (
            <ConfirmationDialog
              open={
                openConfirmationDialog ||
                showWarningDialog ||
                showBulkStatusUpdateWarningDialog ||
                showBulkSendToSAPWarningDialog
              }
              title={
                showWarningDialog
                  ? t('common.label.warning', 'Warning')
                  : openConfirmationDialog
                  ? t('order.deleteOrder', 'Delete Order')
                  : showBulkStatusUpdateWarningDialog
                  ? t('order.changeOrderStatus', 'Change Order Status')
                  : t('order.sendToSAP', 'Send To SAP')
              }
              message={
                showWarningDialog
                  ? statusChangeWarningMessage
                  : openConfirmationDialog
                  ? t(
                      'order.deleteOrderConfirmationMsg',
                      'Are you sure you want to delete this order?'
                    )
                  : showBulkStatusUpdateWarningDialog
                  ? bulkStatusChangeWarningMessage
                  : sendToSAPWarningMessage
              }
              handleClose={
                showWarningDialog
                  ? handleStatusChangeWarningDialogClose
                  : openConfirmationDialog
                  ? handleConfirmationDialogClose
                  : handleBulkStatusUpdateConfirmationDialogClose
              }
              handleDelete={
                showWarningDialog
                  ? onValidationWarningConfirm
                  : openConfirmationDialog
                  ? () => handleOrderDelete(true)
                  : showBulkStatusUpdateWarningDialog
                  ? handleBulkStatusChange
                  : handleSendOrderToSAP
              }
              proceedButtonTitle={
                showWarningDialog ||
                showBulkStatusUpdateWarningDialog ||
                showBulkSendToSAPWarningDialog
                  ? t('common.label.yes', 'Yes')
                  : openConfirmationDialog
                  ? t('common.label.delete', 'Delete')
                  : null
              }
              loading={
                showWarningDialog ? isStatusChangeInProgress : isOrderDeleteInProgress
              }
              cancelButtonTitle={t('common.label.cancel', 'Cancel')}
            />
          ) : null}

          {orderStatusValidationError ? (
            <ConfirmationDialog
              open={orderStatusValidationError}
              title={t('order.validationError', 'Validation Error')}
              message={orderStatusValidationErrorMessage}
              hideCancelButton
              handleClose={handleValidationDialogClose}
              handleDelete={handleValidationDialogClose}
              proceedButtonTitle={t('common.label.ok', 'Ok')}
            />
          ) : null}

          {isEmptyOrderDeletePopupVisible ? (
            <ConfirmationDialog
              open={isEmptyOrderDeletePopupVisible}
              title={t('order.cannotBeConfirmed', 'Cannot Change Status')}
              message={
                emptyOrderValidationErrorMessage ||
                t(
                  'order.emptyOrderValidationErrorMessage',
                  `Order status cannot be changed to "Confirmed" as it does not have any product.`
                )
              }
              hideCancelButton
              handleClose={hideDeleteConfirmationPopup()}
              handleDelete={hideDeleteConfirmationPopup()}
              proceedButtonTitle={t('common.label.ok', 'Ok')}
            />
          ) : null}

          {isOrderValidationIsInProgress || isBackdropOnLoading ? (
            <Portal>
              <>
                <Backdrop open className={'backdrop'} />
                <CircularLoading />
              </>
            </Portal>
          ) : null}
          {openMessageDialog ? (
            <MessageDialog
              open={openMessageDialog}
              title={t('common.label.notAllowed', 'Not Allowed')}
              message={
                sendToSAPWarningMessage ||
                bulkStatusChangeWarningMessage ||
                statusChangeConfirmationMessage ||
                t(
                  'order.status.clientOrDoorInactive',
                  'Cannot change order status as either client / door is inactive'
                )
              }
              handleClose={handleMessageDialogClose}
            />
          ) : null}
          {showRestoreConfirmation && (
            <ConfirmationDialog
              open
              title={t('common.label.restoreOrder', 'Restore Order')}
              message={t(
                'common.label.restoreOrderConfirmation',
                'Are you sure you want to restore this order?'
              )}
              proceedButtonTitle={t('common.label.restore', 'Restore')}
              cancelButtonTitle={t('common.label.cancel', 'Cancel')}
              handleClose={() => handleConfirmationDialogClose()}
              handleDelete={() => handleOrderDelete(false)}
              loading={isOrderDeleteInProgress}
            />
          )}
        </React.Fragment>
      </div>
    )
  })
}
